import type { SearchRequestApi } from '@ingenico/api-sdk-device-management-private-v2';
import { Level, useShowNotification } from '@ingenico/react-notifications';
import {
  useBusinessService,
  useBusinessServiceRequest,
  useCurrentUser,
  useQueryFirstLoading,
} from '@ingenico/react-section-utils';
import type {
  DatagridQuickFilter,
  DataGridSimpleQuickFilter,
  DefaultQuickFilter,
} from '@ingenico/react-ui/advanced-search';
import { DatagridQuickFilterTypeEnum, useQuickFilters } from '@ingenico/react-ui/advanced-search';
import { SEARCH_DEBOUNCE_TIMEOUT } from '@ingenico/react-ui/consts';
import { AlertPropsSeverity } from '@ingenico/react-ui/core-alert';
import { CoreButton } from '@ingenico/react-ui/core-button';
import { DATAGRID_DEFAULT_PAGE_SIZE } from '@ingenico/react-ui/datagrid';
import type { OnTriggerProps, UseDatagridQueryTriggerProps } from '@ingenico/react-ui/hooks/use-datagrid-query-trigger';
import { useDatagridQueryTrigger } from '@ingenico/react-ui/hooks/use-datagrid-query-trigger';
import { useDeleteModalConfirmation } from '@ingenico/react-ui/hooks/use-delete-modal-confirmation';
import type { EmptyListProps } from '@ingenico/react-ui/notice-card';
import { downloadFile, requestAndUpdateState } from '@ingenico/react-utils/functions';
import { useAsyncDataState } from '@ingenico/react-utils/hooks';
import { ALL_LNGS_NAMESPACES, LNGS_NAMESPACE_RESOURCES } from '@ingenico/util-i18n-config';
import AddIcon from '@mui/icons-material/Add';
import type { GridApiPremium, GridSortModel } from '@mui/x-data-grid-premium';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { useDebouncedCallback } from '@react-hookz/web';
import { isEmpty, isEqual, toArray, toString } from 'lodash-es';
import { createElement, useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceManagementService } from '../../domain/business/services/deviceManagement.service';
import type { ResourceListItemFront } from '../../domain/models/resources.model';
import { useDeviceManagementCapability, useDeviceManagementError } from '../../hooks';
import { useTriggerEditResourceRequest } from '../../hooks/useTriggerEditResourceRequest.hook';
import type {
  EditResourceModal,
  ResourceListData,
  ResourcesListGridProps,
  ResourcesListVMOutput,
  UseResourcesListColumnsInput,
} from './ResourcesList.model';
import {
  buildEmptyResourceQuickFilter,
  computeFilters,
  computeSort,
  defaultResourcesGridSortModel,
  isResourceOwnedByOrganization,
} from './ResourcesList.util';
import { useResourcesListColumns } from './useResourcesListColumns.hook';

const EMPTY_RESOURCE_TYPE_FILTER_VALUE = '__EMPTY_RESOURCE_TYPE__';

export function useResourcesListVM(
  params?: Pick<UseResourcesListColumnsInput, 'canCreate' | 'onCreateResource'>,
): ResourcesListVMOutput {
  const { t } = useTranslation(Object.values(LNGS_NAMESPACE_RESOURCES));
  const currentUser = useCurrentUser();
  const showNotification = useShowNotification();
  const deviceManagementService = useBusinessService(DeviceManagementService);
  const resourcesDeleteCapabilityEnabled = useDeviceManagementCapability('resources.delete');
  const resourcesListCapabilityEnabled = useDeviceManagementCapability('resources.list');
  const resourcesEditCapabilityEnabled = useDeviceManagementCapability('resources.edit');
  const canDisplayActions = resourcesListCapabilityEnabled;

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [quickFilters, setQuickFilters] = useState<DatagridQuickFilter[]>([]);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(DATAGRID_DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState<GridSortModel>(defaultResourcesGridSortModel);
  const [page, setPage] = useState(0);
  const [resourceIdToDelete, setResourceIdToDelete] = useState<string>('');
  const [deleteResourceState, setDeleteResourceState] = useAsyncDataState();
  const selectedQuickFilters = useMemo<string[]>(() => {
    if (isEmpty(quickFilters)) {
      return [];
    }

    const [filter] = quickFilters;
    const { input } = filter as DataGridSimpleQuickFilter;
    const { criteria } = input;
    const selectedFilterValue = (criteria as string[])[0] ?? '';
    return selectedFilterValue === EMPTY_RESOURCE_TYPE_FILTER_VALUE ? [''] : [selectedFilterValue];
  }, [quickFilters]);

  const currentUserOrganizationId = useMemo(() => toString(currentUser?.parent?.id), [currentUser?.parent?.id]);

  const canDeleteResource = useCallback(
    (resource: ResourceListItemFront): boolean => {
      if (!resourcesDeleteCapabilityEnabled || !resourcesListCapabilityEnabled || isEmpty(currentUserOrganizationId)) {
        return false;
      }

      return isResourceOwnedByOrganization({
        currentOrganizationId: currentUserOrganizationId,
        resourceOrganizationId: resource.organizationId,
      });
    },
    [currentUserOrganizationId, resourcesDeleteCapabilityEnabled, resourcesListCapabilityEnabled],
  );

  const canEditResource = useCallback(
    (resource?: ResourceListItemFront): boolean => {
      if (
        !resourcesEditCapabilityEnabled ||
        !resourcesListCapabilityEnabled ||
        isEmpty(currentUserOrganizationId) ||
        !resource
      ) {
        return false;
      }

      return isResourceOwnedByOrganization({
        currentOrganizationId: currentUserOrganizationId,
        resourceOrganizationId: resource.organizationId,
      });
    },
    [currentUserOrganizationId, resourcesEditCapabilityEnabled, resourcesListCapabilityEnabled],
  );
  const { getErrorMessage } = useDeviceManagementError();
  const [selectedResource, setSelectedResource] = useState<ResourceListItemFront | null>(null);

  const datagridRef = useGridApiRef();

  const onClickEditResource = useCallback((resource: ResourceListItemFront) => {
    setSelectedResource(resource);
  }, []);

  const isRefetchListProcess = useRef(false);
  const isDeletingSingleRow = useRef(false);

  const fieldsQuery = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService, isRefetch) =>
        deviceManagementService.getResourceFieldValues('resource.extension', isRefetch),
      [],
    ),
  );

  const {
    isLoading: isLoadingFieldValues,
    data: quickFilterValues = [],
    isSuccess: isSuccessFieldValues,
    error: errorFields,
    refetch: refetchQuickFilterValues,
  } = useMemo(() => fieldsQuery, [fieldsQuery]);
  const isFirstLoadingFieldValues = useQueryFirstLoading(fieldsQuery);
  const isFetchingFieldValues = useMemo(
    () => !isFirstLoadingFieldValues && isLoadingFieldValues,
    [isFirstLoadingFieldValues, isLoadingFieldValues],
  );

  const query = useMemo(() => {
    return {
      body: {
        filters: computeFilters({ searchQuery, quickfilterValue: selectedQuickFilters[0] }),
        sort: computeSort(sort),
      } satisfies SearchRequestApi,
      params: { offset, limit },
    };
  }, [searchQuery, selectedQuickFilters, sort, offset, limit]);

  const resourcesListQuery = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService, isRefetch) =>
        isFirstLoadingFieldValues || isDeletingSingleRow.current || isRefetchListProcess.current
          ? null
          : deviceManagementService.getResourcesList({
              body: query.body,
              params: query.params,
              isRefetch,
            }),
      [query, isFirstLoadingFieldValues],
    ),
  );

  const handleRefetchList = useCallback(() => {
    isRefetchListProcess.current = true;
    refetchQuickFilterValues();
  }, [refetchQuickFilterValues]);

  useEffect(() => {
    if (isRefetchListProcess.current && !isLoadingFieldValues && (isSuccessFieldValues || errorFields)) {
      isRefetchListProcess.current = false;
      resourcesListQuery.refetch();
    }
  }, [resourcesListQuery, isLoadingFieldValues, isSuccessFieldValues, errorFields]);

  const { data: apiData, ...otherApiProperties } = resourcesListQuery;
  const isFirstLoading = useQueryFirstLoading(resourcesListQuery);

  const defaultQuickFilters = useCallback(
    () =>
      [
        {
          order: 0,
          field: 'type',
          label: t('field.type', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          options: [],
          searchMode: 'server',
        },
      ] as DefaultQuickFilter[],
    [t],
  );

  const { filterRefApi, isLoadingQuickFilters, fields } = useQuickFilters({
    defaultQuickFilters: defaultQuickFilters(),
    getLabel: useCallback((name: string) => (name === EMPTY_RESOURCE_TYPE_FILTER_VALUE ? '' : name), []),
  });

  const triggerRefetchAfterDeleteSingleRow = useCallback(() => {
    isDeletingSingleRow.current = false;
    handleRefetchList();
  }, [handleRefetchList]);

  const checkCleanQuickFilter = useCallback(() => {
    if (apiData?.pagination?.totalResults === 1) {
      isDeletingSingleRow.current = true;
      setQuickFilters(defaultQuickFilters().map(buildEmptyResourceQuickFilter));
      filterRefApi.current?.resetQuickFilterValue('type');
    } else {
      handleRefetchList();
    }
  }, [apiData?.pagination?.totalResults, defaultQuickFilters, filterRefApi, handleRefetchList]);

  useEffect(() => {
    if (isDeletingSingleRow.current) {
      triggerRefetchAfterDeleteSingleRow();
    }
  }, [quickFilters, triggerRefetchAfterDeleteSingleRow]);

  const handleDeleteResourceRequest = useCallback(async (): Promise<void> => {
    if (isEmpty(resourceIdToDelete)) {
      return;
    }

    await new Promise((resolve, reject) => {
      requestAndUpdateState(deviceManagementService.deleteResource(resourceIdToDelete), setDeleteResourceState, {
        onSuccess: () => {
          checkCleanQuickFilter();
          showNotification({
            level: Level.Success,
            content: t('success.deleteResource', { ns: LNGS_NAMESPACE_RESOURCES.DEFAULT }),
          });
          resolve(void 0);
        },
        onError: (error) => {
          showNotification({
            level: Level.Error,
            content: t('error.default', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          });
          reject(error);
        },
      });
    });
  }, [deviceManagementService, resourceIdToDelete, setDeleteResourceState, showNotification, t, checkCleanQuickFilter]);

  const { handleToggleDeleteDialog: handleToggleDeleteResourceDialog, deleteConfirmationComponentChildren } =
    useDeleteModalConfirmation({
      deleteAction: handleDeleteResourceRequest,
      titleKey: t('confirmation.deleteResource.title', { ns: LNGS_NAMESPACE_RESOURCES.DEFAULT }),
      alertTitle: t('confirmation.deleteResource.warning', { ns: LNGS_NAMESPACE_RESOURCES.DEFAULT }),
      i18nNamespaces: [ALL_LNGS_NAMESPACES.COMMON, LNGS_NAMESPACE_RESOURCES.DEFAULT],
      confirmLabelKey: 'button.delete',
      cancelLabelKey: 'button.cancel',
      hasIconToClose: true,
      hideNotifications: true,
      severity: AlertPropsSeverity.Warning,
      isDeleteLoading: deleteResourceState.isLoading,
      callbackAfterDelete: () => setResourceIdToDelete(''),
      cancelAction: () => setResourceIdToDelete(''),
    });

  const handleDeleteResource = useCallback(
    (resource: ResourceListItemFront): void => {
      setResourceIdToDelete(resource.id);
      handleToggleDeleteResourceDialog();
    },
    [handleToggleDeleteResourceDialog],
  );

  const [, setDownloadResourceState] = useAsyncDataState();

  const handleDownloadResource = useCallback(
    (resource: ResourceListItemFront): void => {
      requestAndUpdateState(deviceManagementService.getDataFileArtifactBlob(resource.id), setDownloadResourceState, {
        onSuccess: (artifactBlob: Blob) => {
          downloadFile(artifactBlob, resource.filename);
          showNotification({
            level: Level.Success,
            content: t('success.downloadResource', { ns: LNGS_NAMESPACE_RESOURCES.DEFAULT }),
          });
        },
        onError: () => {
          showNotification({
            level: Level.Error,
            content: t('error.default', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          });
        },
      });
    },
    [deviceManagementService, setDownloadResourceState, showNotification, t],
  );

  const [, setPreviewResourceState] = useAsyncDataState();

  const handlePreviewResource = useCallback(
    (resource: ResourceListItemFront): void => {
      requestAndUpdateState(deviceManagementService.getDataFileArtifactBlob(resource.id), setPreviewResourceState, {
        onSuccess: (artifactBlob: Blob) => {
          const mime = (resource.mimeType || artifactBlob.type)?.toLowerCase() ?? '';

          if (mime.startsWith('image/')) {
            const previewTab = window.open('', '_blank');
            if (!previewTab) return;
            const typedBlob = resource.mimeType ? new Blob([artifactBlob], { type: resource.mimeType }) : artifactBlob;
            const url = URL.createObjectURL(typedBlob);
            const img = previewTab.document.createElement('img');
            img.style.cssText = 'max-width:100%;height:auto;display:block;margin:0 auto;';
            img.onload = () => URL.revokeObjectURL(url);
            img.onerror = () => URL.revokeObjectURL(url);
            img.src = url;
            previewTab.document.body.style.margin = '0';
            previewTab.document.body.innerHTML = '';
            previewTab.document.body.appendChild(img);
            if (resource.filename) previewTab.document.title = resource.filename;
            return;
          }

          if (mime === 'application/pdf') {
            const typedBlob = resource.mimeType ? new Blob([artifactBlob], { type: resource.mimeType }) : artifactBlob;
            const url = URL.createObjectURL(typedBlob);
            const previewTab = window.open(url, '_blank');
            previewTab?.addEventListener('unload', () => URL.revokeObjectURL(url));
            return;
          }

          if (mime.startsWith('text/') || mime.includes('json') || mime.includes('xml') || mime.includes('csv')) {
            const previewTab = window.open('', '_blank');
            if (!previewTab) return;
            void artifactBlob.text().then((text) => {
              const pre = previewTab.document.createElement('pre');
              pre.style.cssText =
                'white-space:pre-wrap;overflow-wrap:anywhere;padding:16px;font-family:monospace;font-size:14px;';
              pre.textContent = text;
              previewTab.document.body.style.margin = '0';
              previewTab.document.body.innerHTML = '';
              previewTab.document.body.appendChild(pre);
              if (resource.filename) previewTab.document.title = resource.filename;
            });
            return;
          }

          // Fallback: unsupported mime type — show message and download link
          const previewTab = window.open('', '_blank');
          if (!previewTab) return;
          const typedBlob = resource.mimeType ? new Blob([artifactBlob], { type: resource.mimeType }) : artifactBlob;
          const url = URL.createObjectURL(typedBlob);
          const container = previewTab.document.createElement('div');
          container.style.cssText = 'padding:24px;font-family:sans-serif;';
          const message = previewTab.document.createElement('p');
          message.textContent = 'Preview not supported for this file type.';
          const link = previewTab.document.createElement('a');
          link.href = url;
          link.download = resource.filename ?? 'download';
          link.textContent = 'Download file';
          previewTab.addEventListener('unload', () => URL.revokeObjectURL(url));
          container.appendChild(message);
          container.appendChild(link);
          previewTab.document.body.innerHTML = '';
          previewTab.document.body.appendChild(container);
          if (resource.filename) previewTab.document.title = resource.filename;
        },
        onError: () => {
          showNotification({
            level: Level.Error,
            content: t('error.default', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          });
        },
      });
    },
    [deviceManagementService, setPreviewResourceState, showNotification, t],
  );

  const { columns } = useResourcesListColumns({
    canDeleteResource,
    canDisplayActions,
    onDeleteResource: handleDeleteResource,
    onDownloadResource: handleDownloadResource,
    onPreviewResource: handlePreviewResource,
    onClickEditResource,
    canEditResource,
  });

  const gridData = useMemo<ResourceListData>(
    () => ({
      data: toArray(apiData?.data),
      isFetching: otherApiProperties.isLoading || isFetchingFieldValues,
      isLoading: isFirstLoading,
      error: otherApiProperties.error,
      isSuccess: otherApiProperties.isSuccess,
      rowCount: apiData?.pagination?.totalResults ?? 0,
      refetch: handleRefetchList,
    }),
    [apiData, isFirstLoading, otherApiProperties, handleRefetchList, isFetchingFieldValues],
  );

  const noRowsProps = useMemo<EmptyListProps>(
    () => ({
      image: 'empty-bulk',
      isBordered: false,
      messageLabel: 'emptyResources.message',
      namespace: LNGS_NAMESPACE_RESOURCES.DEFAULT,
      titleLabel: 'emptyResources.title',
      content:
        params?.canCreate && params?.onCreateResource
          ? createElement(
              CoreButton,
              {
                variant: 'contained',
                startIcon: createElement(AddIcon),
                onClick: params.onCreateResource,
              },
              t('button.create', { ns: ALL_LNGS_NAMESPACES.COMMON }),
            )
          : undefined,
    }),
    [params?.canCreate, params?.onCreateResource, t],
  );

  const onTrigger = useCallback(({ page: pageCallback, pageSize, sort: newSort }: OnTriggerProps) => {
    setOffset(pageCallback * pageSize);
    setLimit(pageSize);
    setSort(newSort);
    setPage(pageCallback);
  }, []);

  const total = useMemo<number>(() => apiData?.pagination?.totalResults ?? 0, [apiData?.pagination?.totalResults]);

  const serverData = useMemo<UseDatagridQueryTriggerProps['serverData']>(() => {
    return {
      offset,
      limit,
      total,
      sort,
      page,
    };
  }, [limit, offset, page, sort, total]);

  const datagridTriggers = useDatagridQueryTrigger({
    datagridRef: datagridRef as MutableRefObject<GridApiPremium>,
    onTrigger,
    serverData,
    filters: { searchQuery, advancedSearchFields: [] },
  });

  const getRowId = useCallback((resource: ResourceListItemFront) => toString(resource.id), []);

  const gridProps = useMemo<ResourcesListGridProps>(
    () => ({
      ref: datagridRef as MutableRefObject<GridApiPremium>,
      getRowId,
      handlePaginationModelChange: datagridTriggers.onPaginationModelChange,
      handleSortModelChange: datagridTriggers.onSortModelChange,
      sortModel: sort,
      pageSize: limit,
      page,
    }),
    [
      datagridRef,
      datagridTriggers.onPaginationModelChange,
      datagridTriggers.onSortModelChange,
      getRowId,
      limit,
      page,
      sort,
    ],
  );

  const handleSearchQueryChange = useDebouncedCallback(
    (searchValue: string) => setSearchQuery(searchValue),
    [],
    SEARCH_DEBOUNCE_TIMEOUT,
  );

  const quickFilterCall = useCallback(async () => {
    return Promise.resolve(
      quickFilterValues.map((value) => {
        if (value === '') {
          return { label: '', value: EMPTY_RESOURCE_TYPE_FILTER_VALUE };
        }

        return { label: value, value };
      }),
    );
  }, [quickFilterValues]);

  const handleQuickFilterChange = useCallback((newQuickFilters: DatagridQuickFilter[]) => {
    setQuickFilters((currentQuickFilters) =>
      isEqual(currentQuickFilters, newQuickFilters) ? currentQuickFilters : newQuickFilters,
    );
  }, []);

  const overrideDefaultFilterTypes = useCallback(
    (field: string, currentType: DatagridQuickFilterTypeEnum): DatagridQuickFilterTypeEnum => {
      if (field === 'type' && currentType === DatagridQuickFilterTypeEnum.AutoComplete) {
        return DatagridQuickFilterTypeEnum.SingleSelect;
      }

      return currentType;
    },
    [],
  );

  const quickFiltersOptionListRequests = useMemo(
    () => ({
      type: quickFilterCall,
    }),
    [quickFilterCall],
  );

  const quickFiltersKey = useMemo(() => quickFilterValues?.join('|') ?? '|', [quickFilterValues]);

  const quickFiltersSearch = useMemo<ResourcesListVMOutput['quickFiltersSearch']>(
    () => ({
      key: quickFiltersKey,
      filterRefApi,
      isLoading: isLoadingQuickFilters || isLoadingFieldValues,
      isQuickFilterLoading: {
        type: isLoadingQuickFilters || isLoadingFieldValues,
      },
      fields,
      quickFiltersOptionListRequests,
      handleQuickFilterChange,
      overrideDefaultFilterTypes,
      checkAllowReset: (_filter?: string): boolean => true,
      getLabelQuickFilterLabel: (name: string): string => (name === EMPTY_RESOURCE_TYPE_FILTER_VALUE ? '' : name),
      quickFilters,
    }),
    [
      fields,
      filterRefApi,
      handleQuickFilterChange,
      isLoadingFieldValues,
      isLoadingQuickFilters,
      overrideDefaultFilterTypes,
      quickFiltersKey,
      quickFiltersOptionListRequests,
      quickFilters,
    ],
  );

  const shouldDisplayGrid = useMemo<boolean>(
    () => (!!gridData.isSuccess || !!gridData.isFetching) && !isFirstLoading,
    [gridData, isFirstLoading],
  );

  const handleCloseEditModal = useCallback(() => {
    setSelectedResource(null);
  }, []);

  const handleConfirmEditModal = useCallback(() => {
    showNotification({
      content: t('success.editResource', { ns: LNGS_NAMESPACE_RESOURCES.DEFAULT }),
      level: Level.Success,
    });
    setSelectedResource(null);
    handleRefetchList();
  }, [handleRefetchList, showNotification, t]);

  const {
    editResource,
    isLoading: isEditing,
    error,
    reset: resetEditResourceRequest,
  } = useTriggerEditResourceRequest({
    onSuccess: handleConfirmEditModal,
  });

  useEffect(() => {
    resetEditResourceRequest();
  }, [selectedResource, resetEditResourceRequest]);

  const editResourceModal = useMemo<EditResourceModal>(
    () => ({
      isOpen: !!selectedResource,
      onClose: handleCloseEditModal,
      resource: selectedResource,
      onConfirm: editResource,
      errorMessage: error ? getErrorMessage(error) : undefined,
      isLoading: isEditing,
    }),
    [selectedResource, handleCloseEditModal, editResource, isEditing, error, getErrorMessage],
  );

  return {
    advancedSearch: {
      searchQuery,
      handleSearchQueryChange,
    },
    quickFiltersSearch,
    columns,
    deleteConfirmationComponentChildren,
    errorFields,
    gridProps,
    noRowsProps,
    t,
    isFirstLoading,
    shouldDisplayGrid,
    response: gridData,
    editResourceModal,
  };
}
